import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { track } from '../analytics/posthog'
import { tr } from '../i18n'

type Placement = "result" | "download" | "footer";

export function oneMvUrl(placement: Placement): string {
  const url = new URL("https://1mv.ai/");
  if (placement === "download") {
    url.search = new URLSearchParams({
      utm_source: "downloadsuno.com",
    }).toString();
    return url.href;
  }
  url.search = new URLSearchParams({
    utm_source: "downloadsuno.com",
  }).toString();
  return url.href;
}

export function trackOneMvClick(
  placement: Placement,
  element: "cta" | "logo" = "cta",
) {
  track("one_mv_cta_clicked", {
    placement,
    link_id: `one_mv_${placement}_${element}`,
    element_type: element,
    destination: "1mv.ai",
    destination_url: oneMvUrl(placement),
  });
}

export function OneMvLink({
  placement,
  className,
  label,
}: {
  placement: Placement;
  className?: string;
  label?: string;
}) {
  return (
    <a
      className={className}
      href={oneMvUrl(placement)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackOneMvClick(placement)}
    >
      {label ? tr(label) : tr("Create with 1MV")}{" "}
      <span aria-hidden="true">↗</span>
    </a>
  );
}

export function OneMvCard() {
  const titleId = useId();
  useEffect(() => {
    track("one_mv_promo_viewed", { placement: "result" });
  }, []);
  return (
    <aside className="one-mv-card" aria-labelledby={titleId}>
      <div className="one-mv-card-copy">
        <img
          className="one-mv-logo"
          src="/1mv-logo.svg"
          alt="1MV"
          width="71"
          height="20"
        />
        <h3 id={titleId}>
          {tr("Every great song deserves a great music video.")}
        </h3>
        <p>
          {tr(
            "Turn your song into an AI music video with 1MV—in just one click.",
          )}
        </p>
      </div>
      <OneMvLink
        placement="result"
        className="one-mv-cta"
        label="Create a music video for free"
      />
    </aside>
  );
}

export function OneMvDownloadDialog({
  onClose,
}: {
  title?: string;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const dialog = dialogRef.current!;
    const previousOverflow = document.body.style.overflow;
    returnFocusRef.current ??=
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    document.body.style.overflow = "hidden";
    if (!dialog.open) dialog.showModal();
    track("one_mv_promo_viewed", { placement: "download" });
    return () => {
      // Removing the dialog closes it. Calling close() here also emits a close
      // event during StrictMode's effect replay and dismisses the reopened modal.
      document.body.style.overflow = previousOverflow;
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="one-mv-dialog"
      aria-labelledby="one-mv-dialog-title"
      aria-describedby="one-mv-dialog-description"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="one-mv-dialog-panel">
        <div className="one-mv-dialog-top">
          <img
            className="one-mv-logo"
            src="/1mv-logo.svg"
            alt="1MV"
            width="71"
            height="20"
          />
          <button
            type="button"
            className="one-mv-close"
            onClick={onClose}
            aria-label={tr("Close")}
          >
            ×
          </button>
        </div>
        <img
          className="one-mv-demo"
          src="/one-mv-popup-candidate.webp"
          alt={tr("Example music video made with 1MV")}
          width="2942"
          height="1608"
        />
        <div className="one-mv-dialog-copy">
          <h2 id="one-mv-dialog-title">
            {tr("Every great song deserves a great music video.")}
          </h2>
          <p id="one-mv-dialog-description">
            {tr(
              "Turn your song into an AI music video with 1MV—in just one click.",
            )}
          </p>
          <div className="one-mv-dialog-actions">
            <a
              className="one-mv-cta"
              href={oneMvUrl("download")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOneMvClick("download")}
            >
              {tr("Create a music video for free")}{" "}
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
